import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, ArrowUp, Heart } from "lucide-react";

const WhatsIcon = ({ className = "w-4 h-4" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z"/>
    </svg>
);

const PinterestIcon = ({ className = "w-4 h-4" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M12 0C5.4 0 0 5.4 0 12c0 5 3.1 9.3 7.5 11-.1-.9-.2-2.4 0-3.4.2-.9 1.4-5.7 1.4-5.7s-.4-.7-.4-1.8c0-1.7 1-3 2.2-3 1 0 1.5.8 1.5 1.7 0 1-.7 2.6-1 4-.3 1.2.6 2.1 1.7 2.1 2.1 0 3.7-2.2 3.7-5.4 0-2.8-2-4.8-4.9-4.8-3.4 0-5.3 2.5-5.3 5 0 1 .4 2.1.9 2.7.1.1.1.2.1.3-.1.4-.3 1.2-.4 1.4-.1.2-.2.3-.4.2-1.5-.7-2.4-2.9-2.4-4.7 0-3.8 2.8-7.4 8-7.4 4.2 0 7.5 3 7.5 7 0 4.2-2.6 7.5-6.3 7.5-1.2 0-2.4-.6-2.8-1.4l-.8 2.9c-.3 1-1 2.4-1.5 3.2.7.2 1.5.3 2.3.3 6.6 0 12-5.4 12-12S18.6 0 12 0z"/>
    </svg>
);

const QUICK_LINKS = [
    { label: "Home", href: "/" },
    { label: "Office Fit-Out Dubai", href: "/office-fit-out-dubai" },
    { label: "Home Interiors Dubai", href: "/home-interiors-dubai" },
    { label: "Retail Branding Dubai", href: "/retail-branding-dubai" },
    { label: "Restaurant Interiors Dubai", href: "/restaurant-interiors-dubai" },
    { label: "Projects", href: "/#projects" },
    { label: "FAQs", href: "/#faq" },
];

export default function SiteFooter() {
    const [showTop, setShowTop] = useState(false);
    useEffect(() => {
        const onScroll = () => setShowTop(window.scrollY > 600);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    return (
        <footer className="bg-white py-10">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="rounded-[2rem] bg-[#FAFAF5] p-8 sm:p-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
                        {/* Brand */}
                        <div className="lg:col-span-1">
                            <div className="font-display text-2xl font-extrabold leading-tight tracking-tight">
                                EVERY GREAT SPACE STARTS WITH THE RIGHT CHOICES.
                            </div>
                            <p className="mt-4 text-sm text-black/60">
                                Premium interiors, fit-out, branding and finishing for Dubai offices, homes, retail and restaurant spaces.
                            </p>
                            <div className="mt-5 flex gap-3 text-black/50">
                                <span className="text-3xl">🏢</span>
                                <span className="text-3xl">🛋️</span>
                                <span className="text-3xl">🍽️</span>
                            </div>
                        </div>

                        {/* Why */}
                        <div>
                            <h4 className="font-display font-bold text-base">Why Choose 45DaySpaces?</h4>
                            <ul className="mt-4 space-y-2.5 text-sm text-black/70">
                                <li>✅ 45-Day Delivery Plan</li>
                                <li>✅ Transparent Pricing</li>
                                <li>✅ Premium Materials</li>
                                <li>✅ Office, Home &amp; Retail</li>
                                <li>✅ End-to-End Execution</li>
                            </ul>
                        </div>

                        {/* Get in touch */}
                        <div>
                            <h4 className="font-display font-bold text-base">Get In Touch</h4>
                            <ul className="mt-4 space-y-3 text-sm text-black/70">
                                <li className="flex items-start gap-2">
                                    <Mail className="h-4 w-4 mt-0.5 text-[#7e9a00]" />
                                    <a href="mailto:hello@45dayspaces.ae" className="hover:text-black">hello@45dayspaces.ae</a>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Phone className="h-4 w-4 mt-0.5 text-[#7e9a00]" />
                                    <a href="tel:+971552239345" className="hover:text-black">+971 55 223 9345</a>
                                </li>
                                <li className="flex items-start gap-2">
                                    <MapPin className="h-4 w-4 mt-0.5 text-[#7e9a00] shrink-0" />
                                    <span>Pantone 9, 8 27 Street — Al Qouz Ind. Second, Al Quoz, Dubai, UAE</span>
                                </li>
                            </ul>
                        </div>

                        {/* Quick links */}
                        <div>
                            <h4 className="font-display font-bold text-base">Quick Links</h4>
                            <ul className="mt-4 space-y-2.5 text-sm text-black/70">
                                {QUICK_LINKS.map((l) => (
                                    <li key={l.label}><a href={l.href} className="hover:text-black">{l.label}</a></li>
                                ))}
                            </ul>
                        </div>

                        {/* Follow */}
                        <div>
                            <h4 className="font-display font-bold text-base">Follow Us</h4>
                            <div className="mt-4 flex gap-3">
                                <a href="#" aria-label="Facebook" className="h-9 w-9 flex items-center justify-center rounded-full bg-white border border-black/10 hover:bg-[#CCFF00]"><Facebook className="h-4 w-4" /></a>
                                <a href="#" aria-label="Instagram" className="h-9 w-9 flex items-center justify-center rounded-full bg-white border border-black/10 hover:bg-[#CCFF00]"><Instagram className="h-4 w-4" /></a>
                                <a href="#" aria-label="YouTube" className="h-9 w-9 flex items-center justify-center rounded-full bg-white border border-black/10 hover:bg-[#CCFF00]"><Youtube className="h-4 w-4" /></a>
                                <a href="#" aria-label="Pinterest" className="h-9 w-9 flex items-center justify-center rounded-full bg-white border border-black/10 hover:bg-[#CCFF00]"><PinterestIcon className="h-4 w-4" /></a>
                            </div>
                            <a
                                href="https://wa.me/971552239345"
                                target="_blank"
                                rel="noreferrer"
                                className="mt-5 flex items-start gap-3 rounded-2xl bg-[#CCFF00]/30 border border-[#CCFF00] p-3"
                            >
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white shrink-0">
                                    <WhatsIcon className="h-4 w-4" />
                                </span>
                                <div className="text-xs">
                                    <div className="font-bold">Chat with us on WhatsApp</div>
                                    <div className="text-black/60">Get a response in 15 secs!</div>
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* Bottom bar */}
                    <div className="mt-10 border-t border-black/10 pt-5 flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="text-xs text-black/55">© 2026 45DaySpaces. All Rights Reserved.</div>
                        <div className="text-xs text-black/55 inline-flex items-center gap-1.5">
                            <Heart className="h-3.5 w-3.5 text-black/40" fill="currentColor" />
                            Designed with love, built for beautiful living.
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll to top */}
            {showTop && (
                <button
                    onClick={scrollTop}
                    aria-label="scroll to top"
                    data-testid="scroll-top-btn"
                    className="fixed bottom-24 right-5 z-50 h-11 w-11 flex items-center justify-center rounded-full bg-black text-white shadow-lg hover:bg-[#CCFF00] hover:text-black transition-colors"
                >
                    <ArrowUp className="h-5 w-5" />
                </button>
            )}
        </footer>
    );
}
